import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Tag,
  Image,
  Row,
  Col,
  Statistic,
  Typography,
  Button,
  Select,
  message,
  Descriptions,
  Divider,
  Timeline,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Input,
  Space,
  Upload,
} from "antd";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { LineWave } from "react-loader-spinner";
import {
  DownloadOutlined,
  SafetyCertificateOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  MoneyCollectOutlined,
  UploadOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import AccountsService from "../utils/accountsService";
import CollateralSalesService from "../utils/collateralSalesService";
import { uploadFile } from "../utils/firebase";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CollateralProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useContext(AppContext);
  const [collateral, setCollateral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [form] = Form.useForm();
  const token = localStorage.getItem("token");

  const statusOptions = [
    {
      value: "Pending Verification",
      label: "Pending Verification",
      color: "orange",
    },
    { value: "Active", label: "Active", color: "green" },
    { value: "Repossessed", label: "Repossessed", color: "volcano" },
    { value: "Sold", label: "Sold", color: "red" },
    { value: "Released", label: "Released", color: "blue" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collateralResponse, accountsData] = await Promise.all([
          axios.get(
            `https://zedlenders.pickmesms.com/items/collateral_register/${id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
          AccountsService.fetchAccounts(company.id, token),
        ]);

        const data = {
          ...collateralResponse.data.data,
          documents:
            typeof collateralResponse.data.data.documents === "string"
              ? JSON.parse(collateralResponse.data.data.documents)
              : collateralResponse.data.data.documents || [],
          inspection_document:
            collateralResponse.data.data.inspection_document || null,
        };

        setCollateral(data);
        setAccounts(accountsData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        message.error("Failed to load collateral details");
        navigate("/collateral");
      }
    };

    fetchData();
  }, [id, token, navigate, company]);

  const handleStatusUpdate = async (newStatus) => {
    // If trying to mark as sold, show the sale modal instead
    if (newStatus === "Sold") {
      setShowSaleModal(true);
      form.setFieldsValue({
        initial_value: collateral.valuation_amount,
        date_sold: dayjs(),
      });
      return;
    }

    try {
      setUpdating(true);
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/collateral_register/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCollateral((prev) => ({ ...prev, status: newStatus }));
      message.success("Status updated successfully");
    } catch (error) {
      message.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleSaleSubmit = async (values) => {
    try {
      setProcessing(true);

      const saleData = {
        collateral_id: id,
        sale_amount: values.sale_amount,
        initial_value: collateral.valuation_amount,
        account_id: values.account_id,
        related_loan_id: collateral.loan,
        date_sold: values.date_sold.format("YYYY-MM-DD"),
        summary: values.summary || `Sale of collateral: ${collateral.description}`,
        company_id: company.id,
      };

      // Calculate profit/loss
      const profitLoss = parseFloat(values.sale_amount) - parseFloat(collateral.valuation_amount);

      // Record the sale through the service
      await CollateralSalesService.recordSale(saleData, token);

      // Also update the collateral record directly with profit_loss
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/collateral_register/${id}`,
        {
          status: "Sold",
          sale_amount: parseFloat(values.sale_amount),
          sale_date: values.date_sold.format("YYYY-MM-DD"),
          sale_summary: values.summary || `Sale of collateral: ${collateral.description}`,
          profit_loss: profitLoss
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update related loan status to 'settled' since collateral has been sold
      if (collateral.loan) {
        try {
          await axios.patch(
            `https://zedlenders.pickmesms.com/items/loans/${collateral.loan}`,
            {
              status: "settled",
              settled_date: values.date_sold.format("YYYY-MM-DD"),
              settlement_notes: `Loan settled through collateral sale. ${values.summary || ''}`
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Loan status updated to 'settled'");
        } catch (loanError) {
          console.error("Error updating loan status:", loanError);
          message.warning("Collateral sold but failed to update loan status");
        }
      }

      // Refresh accounts to get updated balance
      const updatedAccounts = await AccountsService.fetchAccounts(company.id, token);
      setAccounts(updatedAccounts);

      // Update local state
      setCollateral((prev) => ({
        ...prev,
        status: "Sold",
        sale_amount: values.sale_amount,
        sale_date: values.date_sold.format("YYYY-MM-DD"),
        profit_loss: profitLoss,
      }));

      message.success("Collateral marked as sold successfully!");
      setShowSaleModal(false);
      form.resetFields();
    } catch (error) {
      console.error("Error recording sale:", error);
      message.error("Failed to record collateral sale");
    } finally {
      setProcessing(false);
    }
  };

  const handleModalCancel = () => {
    setShowSaleModal(false);
    form.resetFields();
  };

  const handleDownloadDocument = () => {
    if (collateral.inspection_document) {
      // Create temporary link to trigger download
      const link = document.createElement("a");
      link.href = collateral.inspection_document;
      link.download = `collateral-inspection-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      message.warning("No inspection document available");
    }
  };

  const handleUploadImages = async () => {
    if (uploadFileList.length === 0) {
      message.warning("Please select at least one image to upload");
      return;
    }

    try {
      setUploadingImages(true);
      
      // Upload files to Firebase
      const uploadPromises = uploadFileList.map(file => 
        uploadFile(file.originFileObj, `collateral-${id}`)
      );
      
      const newImageUrls = await Promise.all(uploadPromises);
      
      // Get current documents and add new ones
      const currentDocs = collateral.documents || [];
      const updatedDocs = [...currentDocs, ...newImageUrls];
      
      // Update collateral record with new documents
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/collateral_register/${id}`,
        { documents: JSON.stringify(updatedDocs) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setCollateral(prev => ({
        ...prev,
        documents: updatedDocs
      }));
      
      message.success(`Successfully uploaded ${newImageUrls.length} image(s)`);
      setShowUploadModal(false);
      setUploadFileList([]);
      
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  if (loading) {
    return (
      <div className="center-loader">
        <LineWave
          visible={true}
          height="100"
          width="100"
          color="#36454F"
          ariaLabel="line-wave-loading"
        />
      </div>
    );
  }

  return (
    <div className="collateral-profile">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ marginBottom: 0 }}>
            <SafetyCertificateOutlined /> {collateral.description}
          </Title>
          <Text type="secondary">Collateral ID: {id}</Text>
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadDocument}
            disabled={!collateral.inspection_document}
          >
            Inspection Report
          </Button>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={16}>
          <Card title="Asset Details">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Valuation Amount">
                <Text strong>
                  <DollarOutlined /> ZMW{" "}
                  {collateral.valuation_amount?.toLocaleString()}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Current Status">
                <Tag
                  color={
                    statusOptions.find((s) => s.value === collateral.status)
                      ?.color
                  }
                >
                  {collateral.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Associated Loan">
                {collateral.loan ? (
                  <Tag color="blue">{collateral.loan}</Tag>
                ) : (
                  <Text type="secondary">N/A</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Registration Date">
                {collateral.registration_date
                  ? new Date(collateral.registration_date).toLocaleDateString()
                  : collateral.date_created
                  ? new Date(collateral.date_created).toLocaleDateString()
                  : "N/A"}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Status Management</Divider>
            <div className="status-update">
              <Select
                value={collateral.status}
                style={{ width: 200, marginRight: 16 }}
                onChange={handleStatusUpdate}
                loading={updating}
                disabled={collateral.status === "Sold"}
              >
                {statusOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    <Tag color={option.color}>{option.label}</Tag>
                  </Option>
                ))}
              </Select>
              {collateral.status === "Sold" ? (
                <Text type="secondary" style={{ color: "#cf1322" }}>
                  Status locked - Collateral has been sold
                </Text>
              ) : (
                <Text type="secondary">
                  Last updated: {new Date().toLocaleString()}
                </Text>
              )}
            </div>
          </Card>

          <Card title="Collateral" style={{ marginTop: 24 }}>
            <Row gutter={[16, 16]}>
              {collateral.documents?.map((doc, index) => (
                <Col span={8} key={index}>
                  <Card
                    cover={
                      <Image
                        src={doc}
                        alt={`Image ${index + 1}`}
                        style={{ height: 200, objectFit: "cover" }}
                      />
                    }
                    actions={[
                      <a href={doc} target="_blank" rel="noopener noreferrer">
                        View Full Size
                      </a>,
                    ]}
                  >
                    <Card.Meta title={`Image ${index + 1}`} />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col span={8}>
          <Card title="Quick Actions">
            <Button
              block
              type="primary"
              icon={<PictureOutlined />}
              style={{ marginBottom: 16 }}
              onClick={() => setShowUploadModal(true)}
              disabled={collateral.status === "Sold"}
            >
              Upload New Pictures
            </Button>
            <Button
              block
              type="default"
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 16 }}
              onClick={() => handleStatusUpdate("Active")}
              disabled={collateral.status === "Sold"}
            >
              Mark as Active
            </Button>
            {/* <Button
              block
              danger
              icon={<FilePdfOutlined />}
              style={{ marginBottom: 16 }}
              onClick={handleDownloadDocument}
            >
              Generate PDF Report
            </Button> */}
            <Button
              block
              type="dashed"
              onClick={() => navigate(`/loan/${collateral.loan}`)}
              disabled={!collateral.loan}
            >
              View Associated Loan
            </Button>
          </Card>

          <Card title="Audit Trail" style={{ marginTop: 24 }}>
            <div className="audit-log">
              <Timeline>
                <Timeline.Item color="green">
                  Created on{" "}
                  {collateral.registration_date
                    ? new Date(
                        collateral.registration_date
                      ).toLocaleDateString()
                    : collateral.date_created
                    ? new Date(collateral.date_created).toLocaleDateString()
                    : "N/A"}
                </Timeline.Item>
                <Timeline.Item color="blue">
                  Last valuation updated: {collateral.valuation_date || "N/A"}
                </Timeline.Item>
                <Timeline.Item color="orange">
                  Last inspection: {collateral.inspection_date || "N/A"}
                </Timeline.Item>
              </Timeline>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Sale Modal */}
      <Modal
        title={`Mark as Sold - ${collateral?.description}`}
        open={showSaleModal}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaleSubmit}
          initialValues={{
            initial_value: collateral?.valuation_amount,
            date_sold: dayjs(),
          }}
        >
          <Form.Item name="initial_value" label="Initial Value (ZMW)">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              disabled
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
            />
          </Form.Item>

          <Form.Item
            name="sale_amount"
            label="Sale Amount (ZMW)"
            rules={[
              { required: true, message: "Please enter the sale amount" },
              { type: "number", min: 0, message: "Amount must be positive" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              placeholder="Enter sale amount"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            name="account_id"
            label="Credit Account"
            rules={[{ required: true, message: "Please select an account" }]}
          >
            <Select placeholder="Select account to credit">
              {accounts.map((account) => (
                <Option key={account.id} value={account.id}>
                  {account.name} - Balance: ZMW{" "}
                  {parseFloat(account.balance || 0).toLocaleString()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="date_sold"
            label="Date Sold"
            rules={[{ required: true, message: "Please select the sale date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="summary" label="Sale Summary (Optional)">
            <TextArea
              rows={3}
              placeholder="Enter details about the sale, buyer information, etc."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={processing}
                icon={<MoneyCollectOutlined />}
              >
                Mark as Sold
              </Button>
              <Button onClick={handleModalCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Images Modal */}
      <Modal
        title="Upload New Pictures"
        open={showUploadModal}
        onCancel={() => {
          setShowUploadModal(false);
          setUploadFileList([]);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setShowUploadModal(false);
            setUploadFileList([]);
          }}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploadingImages}
            onClick={handleUploadImages}
            disabled={uploadFileList.length === 0}
            icon={<UploadOutlined />}
          >
            Upload Images
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Add new pictures to this collateral item. Images will be added to the existing collection.
          </Text>
        </div>
        
        <Upload
          beforeUpload={() => false}
          listType="picture-card"
          multiple
          accept="image/*"
          fileList={uploadFileList}
          onChange={({ fileList }) => setUploadFileList(fileList)}
          maxCount={10}
        >
          {uploadFileList.length < 10 && (
            <div>
              <PictureOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>
        
        {uploadFileList.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text strong>Selected files: {uploadFileList.length}</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CollateralProfile;
